import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  executivePrioritiesClient,
  executivePriorityGrants as grants,
  type PriorityInvoiceScope,
  type PriorityOptionsQuery,
} from '@/services/executivePrioritiesClient';
import type { RequestOptions } from '@/services/apiClient';
import { useScopedNumericQuery } from './useScopedNumericQuery';
import {
  sessionScopeKey,
  useSessionScopeFence,
  type SessionScopeIdentity,
} from './useSessionScope';
const ROOT = 'executive-priorities';
export const useExecutivePriorities = (
  page: number,
  scope?: PriorityInvoiceScope,
  pageSize?: number,
) =>
  useScopedNumericQuery(
    [ROOT, 'list', page, scope?.sourceType ?? '', scope?.sourceId ?? '', pageSize ?? 40],
    grants.read,
    (options) =>
      pageSize !== undefined
        ? executivePrioritiesClient.list(page, options, scope, pageSize)
        : scope
          ? executivePrioritiesClient.list(page, options, scope)
          : executivePrioritiesClient.list(page, options),
    { staleTime: 0, refetchInterval: 60000 },
  );
export const useExecutivePriority = (id: string) =>
  useScopedNumericQuery(
    [ROOT, 'detail', id],
    grants.read,
    (options) => executivePrioritiesClient.detail(id, options),
    { enabled: Boolean(id), staleTime: 0 },
  );
export const useExecutivePriorityOptions = (enabled: boolean, query?: PriorityOptionsQuery) =>
  useScopedNumericQuery(
    [
      ROOT,
      'options',
      query?.q ?? '',
      query?.documentAssetId ?? '',
      query?.sourceType ?? '',
      query?.sourceId ?? '',
      query?.page ?? 1,
      query?.pageSize ?? 40,
    ],
    grants.read,
    (options) => executivePrioritiesClient.options(query, options),
    { enabled, staleTime: 0 },
  );
export function useExecutivePriorityAction<Input, Output>(
  permission: string,
  action: (input: Input, options: RequestOptions) => Promise<Output>,
) {
  const { identity, enabled, isCurrent } = useSessionScopeFence(permission);
  const client = useQueryClient();
  const mutation = useMutation({
    mutationKey: [ROOT, 'mutation', permission, ...sessionScopeKey(identity)],
    retry: false,
    onMutate: ({ origin }: { origin: SessionScopeIdentity; input: Input }) => origin,
    mutationFn: async ({ origin, input }: { origin: SessionScopeIdentity; input: Input }) => {
      if (!enabled || !isCurrent(origin))
        throw new Error('Current executive-priority access required.');
      const result = await action(input, {
        sessionOrigin: origin.sessionOrigin,
        allowRefresh: false,
      });
      if (!isCurrent(origin))
        throw new Error('Sign-in or access changed. Reload to inspect the save outcome.');
      return result;
    },
    onSuccess: async (_result, { origin }) => {
      if (!isCurrent(origin)) return;
      const scope = sessionScopeKey(origin);
      await client.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === ROOT &&
          scope.every(
            (part, index) => query.queryKey[query.queryKey.length - scope.length + index] === part,
          ),
      });
    },
  });
  const visible = Boolean(mutation.context && isCurrent(mutation.context));
  return {
    mutateAsync: (input: Input) => mutation.mutateAsync({ origin: identity, input }),
    error: visible ? mutation.error : null,
    isPending: visible && mutation.isPending,
    isCurrent: () => isCurrent(identity),
  };
}
